<script setup lang="ts">
import type { MegamenuLink, MegamenuSection } from '~/data/megamenu'

const props = defineProps<{
  section: MegamenuSection | null
}>()

const localePath = useLocalePath()

function isExternal(href: string) {
  return href.startsWith('http')
}

function resolveHref(href: string) {
  return isExternal(href) ? href : localePath(href)
}

/** Flatten subSections into column groups for layout */
const columnGroups = computed(() => {
  if (!props.section?.subSections?.length) return null
  return props.section.subSections.map((sub) => sub.items).filter(Boolean)
})

const flatItems = computed(() => {
  if (!props.section) return []
  if (props.section.items?.length)
    return props.section.items.filter((i) => i?.label)
  return []
})
</script>

<template>
  <Transition name="megamenu-fade" mode="out-in">
    <div
      v-if="section"
      :key="section.title"
      class="py-4 grow"
    >
      <!-- Column groups from subSections (e.g. Top categories: Bestseller, Kreativkurse, ...) -->
      <div
        v-if="columnGroups?.length"
        class="grid gap-6 pl-8"
        :class="[
          columnGroups.length === 2 && 'grid-cols-2',
          columnGroups.length === 3 && 'grid-cols-3',
          columnGroups.length === 4 && 'grid-cols-4',
        ]"
      >
        <ul
          v-for="(subSection, subSectionIndex) in section?.subSections"
          :key="subSectionIndex"
          class="flex flex-col gap-4 text-sm sub-section"
          :aria-label="subSection?.title"
        >
          <li v-for="(item, i) in subSection?.items" :key="i">
            <a
              v-if="item.href"
              :href="resolveHref(item.href)"
              class="text-stone-900 hover:text-teal-600 font-medium no-underline"
              :target="isExternal(item.href) ? '_blank' : undefined"
              :rel="isExternal(item.href) ? 'noopener' : undefined"
            >
              {{ item.label }}
            </a>
            <span v-else class="text-stone-500">{{ item.label }}</span>
          </li>
        </ul>
      </div>
      <!-- Single list (items) -->
      <ul
        v-else-if="flatItems.length"
        class="grid gap-x-8 gap-y-4 pl-8 text-sm"
        :class="[
          section?.columns === 2 && 'grid-cols-2',
          section?.columns === 3 && 'grid-cols-3',
          section?.columns === 4 && 'grid-cols-4',
        ]"
      >
        <li v-for="(item, i) in flatItems" :key="i">
          <a
            v-if="item.href"
            :href="resolveHref(item.href)"
            class="text-stone-900 hover:text-teal-600 no-underline"
            :target="isExternal(item.href) ? '_blank' : undefined"
            :rel="isExternal(item.href) ? 'noopener' : undefined"
          >
            {{ item.label }}
          </a>
          <span v-else class="text-stone-500">{{ item.label }}</span>
        </li>
      </ul>
    </div>
  </Transition>
</template>
<style scoped lang="postcss">
.megamenu-fade-enter-active,
.megamenu-fade-leave-active {
  transition: opacity 0.15s ease-out;
}
.megamenu-fade-enter-from,
.megamenu-fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .megamenu-fade-enter-active,
  .megamenu-fade-leave-active {
    transition-duration: 0.01ms;
  }
}

.sub-section:before {
  content: attr(aria-label);
  @apply text-stone-500 font-medium;
}
</style>
